import {
  AfterContentInit,
  Component,
  Inject,
  OnDestroy,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PlatformAppContext } from '@/app/platform/platform-context/platform-app-context';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { Router } from '@angular/router';
import {
  MatDialog,
  MatDialogRef,
  MatDialogState
} from '@angular/material/dialog';
import { LAYOUT_ACTIONS, LAYOUT_QUERIES, LayoutActions, LayoutQueries } from '@core/store/types';
import { CONFIRMER, Confirmer } from '@shared/components/dialogs/confirm/types';
import { openAppQueryParam } from '@/app/platform/platform-core/user/constants';
import { ApplicationService } from '@/app/application.service';
import { ApplicationActionsFacade } from '@core/actions/application-actions-facade';

@Component({
    selector: 'app-egi-app-menu',
    templateUrl: './egi-app-menu.component.html',
    styleUrls: ['./egi-app-menu.component.scss'],
    host: {
        class: 'd-inline-flex align-items-center'
    },
    standalone: false
})
export class EgiAppMenuComponent implements OnDestroy, AfterContentInit {
  @ViewChild('menuDialog', { static: true })
  menuDialogRef: TemplateRef<any>;

  dialogRef?: MatDialogRef<any>;

  constructor(
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    public readonly platformAppContext: PlatformAppContext,
    public router: Router,
    private readonly dialog: MatDialog,
    @Inject(LAYOUT_QUERIES)
    private readonly layoutQueries: LayoutQueries,
    @Inject(LAYOUT_ACTIONS)
    readonly layoutActions: LayoutActions,
    @Inject(CONFIRMER)
    private readonly confirmer: Confirmer,
    readonly appService: ApplicationService,
    private readonly applicationActions: ApplicationActionsFacade
  ) {}

  ngAfterContentInit(): void {
    this.showAppMenuSubscription = this.layoutQueries.showAppMenu$.subscribe(show => {
      if (show) {
        this.showMenu();
      } else {
        this.hideMenu();
      }
    });
  }

  afterCloseSubscription?: Subscription;
  showAppMenuSubscription?: Subscription;
  appSearchText$ = new BehaviorSubject<string>('');

  ngOnDestroy() {
    this.afterCloseSubscription?.unsubscribe();
    this.showAppMenuSubscription?.unsubscribe();
  }

  showMenu(): void {
    if (this.dialogRef?.getState() === MatDialogState.OPEN) {
      return;
    }

    this.afterCloseSubscription?.unsubscribe();
    this.dialogRef = this.dialog.open(this.menuDialogRef, {
      position: {
        top: '72px'
      },
      maxWidth: '440px',
      width: 'min(440px, calc(100vw - 32px))',
      maxHeight: 'calc(100vh - 100px)',
      hasBackdrop: true,
      backdropClass: 'app-menu-backdrop',
      panelClass: 'app-menu-dialog-panel',
      closeOnNavigation: true
    });

    this.afterCloseSubscription = this.dialogRef.afterClosed().subscribe(() => this.closeAppMenu());
  }

  hideMenu(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = undefined;
    }
  }

  autoFocusOnSearch(): boolean {
    return true;
  }

  readonly _appsWithoutSearch$: Observable<UserEgiApp[]> = this.userQueries.availableApps$.pipe(
    map(
      apps =>
        apps.filter(a => a.rootNavigationItem.isBaseAccessAllowed && !a.isPlatformBeheer) ?? []
    )
  );

  readonly allApps$: Observable<UserEgiApp[]> = combineLatest([
    this._appsWithoutSearch$,
    this.appSearchText$
  ]).pipe(
    map(([apps, searchText]) => {
      if (!searchText) {
        return apps;
      }
      const needle = searchText.toLowerCase();
      return apps.filter(
        a =>
          a.name.toLowerCase().includes(needle) ||
          (a.poolName ?? '').toLowerCase().includes(needle)
      );
    })
  );

  isActiveApp$(app: UserEgiApp): Observable<boolean> {
    return this.platformAppContext.activeApp$.pipe(map(currentApp => app.is(currentApp)));
  }

  selectApp(app?: UserEgiApp): void {
    this.closeAppMenu();
    this.router.navigate([app.rootNavigationItem.clone().routerLink], {
      queryParams: { navId: app.rootNavigationItem.id, [openAppQueryParam]: true }
    });
  }

  async reloadWindow() {
    const confirmation = await this.confirmer.confirm(
      'Ben je zeker dat je de applicatie wil herstarten?'
    );
    if (confirmation) {
      window.location.reload();
    }
  }

  closeAppMenu() {
    this.layoutActions.closeAppMenu();
    this.appSearchText$.next('');
  }

  async restartApplication(): Promise<void> {
    await this.applicationActions.resetApplication();
  }
}
